import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, Award, MessageCircle, Search, GitCompare, X, ShoppingBag } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { productsData, categories } from '@/data/productsData';
import { StarRating } from '@/components/StarRating';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const ProductsPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [compareList, setCompareList] = useState([]);
  const { addItem } = useCart();

  const filteredProducts = productsData.filter((product) => {
    const matchesCategory = activeCategory === 'all' || product.category === activeCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleCompare = (productId) => {
    setCompareList(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        if (prev.length >= 4) {
          alert('You can compare up to 4 products at a time');
          return prev;
        }
        return [...prev, productId];
      }
    });
  };

  const handleCompare = () => {
    if (compareList.length < 2) {
      alert('Please select at least 2 products to compare');
      return;
    }
    navigate(`/compare-products?ids=${compareList.join(',')}`);
  };

  const clearCompare = () => {
    setCompareList([]);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <span className="inline-block text-gold font-semibold text-sm uppercase tracking-wider mb-4">OUR PRODUCTS</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
              Premium Fitness
              <span className="block text-primary">Supplements</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Explore a curated selection of premium fitness supplements sourced directly from authorized distributors. All products are 100% authentic and quality-verified.
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-400">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-medium">100% Authentic</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-400">
                <Award className="w-4 h-4" />
                <span className="text-sm font-medium">Certified Products</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4">
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search products or brands..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Category Tabs */}
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-8">
            <TabsList className="flex flex-wrap gap-2 h-auto bg-transparent p-0">
              {categories.map((category) => (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className="px-4 py-2 rounded-full border border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary"
                >
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Card key={product.id} className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-2 flex flex-col relative">
                {/* Compare Checkbox */}
                <div className="absolute top-3 left-3 z-10">
                  <label className="flex items-center gap-2 cursor-pointer bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm hover:bg-white transition-colors" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={compareList.includes(product.id)}
                      onChange={() => toggleCompare(product.id)}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                    />
                    <span className="text-xs font-medium text-foreground">Compare</span>
                  </label>
                </div>

                <Link to={`/products/${product.slug}`} className="flex flex-col flex-1" data-testid={`product-card-${product.id}`}>
                  <div className="relative aspect-square overflow-hidden bg-muted">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    {product.popular && (
                      <Badge className="absolute top-3 right-3 bg-gold text-foreground">
                        Popular
                      </Badge>
                    )}
                    <div className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-success/90 flex items-center justify-center">
                      <svg className="w-4 h-4 text-success-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <CardContent className="p-5 flex flex-col flex-1">
                    <span className="text-xs text-gold font-medium uppercase tracking-wider mb-1">{product.brand}</span>
                    <h3 className="font-bold text-foreground mb-1">{product.name}</h3>
                    {/* Star Rating */}
                    <StarRating slug={product.slug} />
                    <p className="text-sm text-muted-foreground mb-4 flex-1">{product.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {product.features.map((feature) => (
                        <span key={feature} className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Link>
                <div className="px-5 pb-5 flex gap-2">
                  <Button
                    variant="default"
                    className="flex-1"
                    onClick={(e) => {
                      e.preventDefault();
                      addItem(product, product.sizes?.[0] || '');
                      toast.success(`${product.name} added to cart`);
                    }}
                    data-testid={`add-cart-btn-${product.id}`}
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Add to Cart
                  </Button>
                  <Button variant="outline" size="icon" asChild>
                    <a href={`https://wa.me/9779824042067?text=Hi!%20I'm%20interested%20in%20${encodeURIComponent(product.name)}`} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-btn-${product.id}`}>
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">No products found</h3>
              <p className="text-muted-foreground mb-4">Try adjusting your search or filter</p>
              <Button variant="outline" onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}>
                Clear Filters
              </Button>
            </div>
          )}

          {/* CTA Banner */}
          <div className="mt-16 p-8 md:p-12 rounded-2xl bg-gradient-to-r from-primary/10 via-gold/10 to-primary/10 border border-primary/20 text-center">
            <h3 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">
              Can't Find What You're Looking For?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              We have a wide range of products in our physical store. Contact us on WhatsApp to enquire about any specific product or brand.
            </p>
            <Button variant="whatsapp" size="lg" asChild>
              <a href="https://wa.me/9779824042067?text=Hi!%20I'm%20looking%20for%20a%20specific%20product" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current mr-2">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Enquire on WhatsApp
              </a>
            </Button>
          </div>

          {/* SEO Content Section */}
          <div className="mt-20 max-w-4xl mx-auto">
            {/* H1 for SEO */}
            <h1 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-6">
              Buy Whey Protein in India – 100% Authentic
            </h1>

            <p className="text-base text-muted-foreground leading-relaxed mb-8">
              Shop premium whey protein supplements in India and Nepal at NutraFuel Supplements.
              Trusted by athletes for muscle growth, recovery, and performance.
            </p>

            {/* Why Choose Section */}
            <h2 className="text-2xl font-display font-bold text-foreground mb-4">
              Why Choose Whey Protein from NutraFuel?
            </h2>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-8">
              <li>Authentic & lab-verified products</li>
              <li>Top Indian & international brands</li>
              <li>Fast delivery across India</li>
            </ul>

            {/* Best For Muscle Gain */}
            <h2 className="text-2xl font-display font-bold text-foreground mb-4">
              Best Whey Protein for Muscle Gain
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed mb-8">
              Our whey protein range supports lean muscle growth, faster recovery,
              and improved strength.
            </p>

            {/* FAQ Section */}
            <h2 className="text-2xl font-display font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Which whey protein is best for beginners?
                </h3>
                <p className="text-muted-foreground">
                  Whey concentrate or blend is ideal for beginners. It provides excellent protein quality at an affordable price point and is easier to digest.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  How much protein do I need per day?
                </h3>
                <p className="text-muted-foreground">
                  For muscle building, aim for 1.6-2.2g of protein per kg of body weight. Whey protein helps you meet your daily protein goals conveniently.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  When should I take whey protein?
                </h3>
                <p className="text-muted-foreground">
                  Post-workout is ideal for muscle recovery. You can also take it as a meal replacement or between meals to increase daily protein intake.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Compare Bar */}
      {compareList.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-primary text-primary-foreground shadow-2xl border-t-2 border-primary-dark animate-fade-up">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <GitCompare className="w-6 h-6" />
                <div>
                  <div className="font-bold text-lg">
                    {compareList.length} Product{compareList.length > 1 ? 's' : ''} Selected
                  </div>
                  <div className="text-sm text-primary-foreground/80">
                    {compareList.length < 2 ? 'Select at least 2 products to compare' : `Compare up to ${4 - compareList.length} more`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearCompare}
                  className="text-primary-foreground hover:text-primary-foreground hover:bg-primary-dark"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleCompare}
                  disabled={compareList.length < 2}
                  className="bg-gold text-foreground hover:bg-gold-light font-bold"
                >
                  <GitCompare className="w-5 h-5 mr-2" />
                  Compare Products
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ProductsPage;
